/**
 * FeatureFlagsContext
 * 
 * Centralized context for user-based feature flags.
 * Feature flags are fetched from the /property/all endpoint based on user ID.
 * This replaces cookie-based feature flags for better security and control.
 */

import { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  useMemo,
  type ReactNode 
} from 'react'
import { useAuth } from './AuthContext'
import { getAllProperties } from '@/lib/api/api'

// ============================================================================
// Types
// ============================================================================

interface FeatureFlags {
  adsAttribution: boolean
  workflows: boolean
  ga4: boolean
}

interface FeatureFlagsContextType {
  flags: FeatureFlags
  isLoading: boolean
  hasFeature: (feature: keyof FeatureFlags) => boolean
}

// ============================================================================
// Default State
// ============================================================================

const defaultFlags: FeatureFlags = {
  adsAttribution: false,
  workflows: false,
  ga4: false,
}

// ============================================================================
// Context
// ============================================================================

const FeatureFlagsContext = createContext<FeatureFlagsContextType | null>(null)

// ============================================================================
// Provider Component
// ============================================================================

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch feature flags from API
  useEffect(() => {
    const fetchFlags = async () => {
      if (!token) {
        setFlags(defaultFlags)
        setIsLoading(false)
        return
      }

      try {
        const data = await getAllProperties(token)
        const apiFlags = data?.featureFlags || {}
        
        setFlags({
          adsAttribution: apiFlags.adsAttribution || false,
          workflows: apiFlags.workflows || false,
          ga4: apiFlags.ga4 || false,
        })
      } catch (error) {
        console.error('[FeatureFlags] Error fetching feature flags:', error)
        setFlags(defaultFlags)
      } finally {
        setIsLoading(false)
      }
    }

    fetchFlags()
  }, [token])

  // Helper to check if user has a specific feature
  const hasFeature = useMemo(() => {
    return (feature: keyof FeatureFlags): boolean => {
      return flags[feature] || false
    }
  }, [flags])

  const value: FeatureFlagsContextType = useMemo(() => ({
    flags,
    isLoading,
    hasFeature,
  }), [flags, isLoading, hasFeature])

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

const defaultContextValue: FeatureFlagsContextType = {
  flags: defaultFlags,
  isLoading: false,
  hasFeature: () => false,
}

export function useFeatureFlags(): FeatureFlagsContextType {
  const context = useContext(FeatureFlagsContext)
  
  if (!context) {
    return defaultContextValue
  }
  
  return context
}

